import React, { useState } from "react";
import MessageBlocks from "components/commsLog/MessageBlocks";
import { useCommsPropertyChatQuery } from "apis/commsLog";
import { formatSydney } from "utils/commsLogStates";

const senderName = (message) =>
  message?.sender?.userId?.name || message?.sender?.model || "Unknown";

const GroupChatPanel = ({ propertyId }) => {
  const [page, setPage] = useState(1);
  const { chat, messages, pagination, isPending, isError } =
    useCommsPropertyChatQuery(propertyId, page);

  if (isPending) {
    return <p className="text-sm text-darkgray">Loading the group chat…</p>;
  }

  if (isError) {
    return <p className="text-sm text-red-600">Could not load the group chat.</p>;
  }

  // A campaign with no group chat has nowhere for its messages to land.
  if (!chat) {
    return (
      <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">
        This property has no vendor group chat. Nothing sent to it can arrive.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        {chat.isArchived ? (
          <span className="rounded-full bg-orange-100 px-2 py-0.5 text-xs text-orange-700">
            Archived group
          </span>
        ) : (
          <span />
        )}
        <span className="text-xs text-darkgray">
          {pagination?.total || 0} messages
        </span>
      </div>

      <div className="max-h-[520px] space-y-2 overflow-y-auto rounded-md border border-mediumgray p-3">
        {messages.length === 0 ? (
          <p className="text-sm italic text-darkgray">No messages yet.</p>
        ) : (
          messages.map((message) => (
            <div
              key={message._id}
              className={`rounded-md p-2.5 ${
                message.isDeleted ? "bg-red-50" : "bg-lightgray"
              }`}
            >
              <div className="mb-1 flex items-start justify-between gap-3">
                <span className="text-xs font-medium">
                  {senderName(message)}
                  {message.metadata?.sourceName ? (
                    <span className="ml-1.5 text-darkgray">
                      · {message.metadata.sourceName}
                    </span>
                  ) : null}
                </span>
                <span className="whitespace-nowrap text-xs text-darkgray">
                  {formatSydney(message.createdAt)}
                </span>
              </div>
              {/* The group renders blocks, so plain `content.text` is not what was seen. */}
              {message.content?.blocks?.length ? (
                <MessageBlocks blocks={message.content.blocks} bare />
              ) : (
                <p className="whitespace-pre-wrap text-sm">
                  {message.content?.text || "(no text)"}
                </p>
              )}
            </div>
          ))
        )}
      </div>

      {pagination?.totalPages > 1 ? (
        <div className="flex items-center justify-between text-xs">
          <button
            type="button"
            className="rounded border border-mediumgray px-2 py-1 disabled:opacity-40"
            disabled={page >= pagination.totalPages}
            onClick={() => setPage((current) => current + 1)}
          >
            Older
          </button>
          <span className="text-darkgray">
            Page {page} of {pagination.totalPages}
          </span>
          <button
            type="button"
            className="rounded border border-mediumgray px-2 py-1 disabled:opacity-40"
            disabled={page <= 1}
            onClick={() => setPage((current) => Math.max(current - 1, 1))}
          >
            Newer
          </button>
        </div>
      ) : null}
    </div>
  );
};

export default GroupChatPanel;
